import { BotaoVoltar } from "@/shared/components/BotaoVoltar";
import { useEffect, useState } from "react";
import { Modal } from "@/shared/components/Modal";
import { AvisoConflito } from "@/modules/escala/components/AvisoConflito";
import { SeletorParticipantes } from "@/modules/escala/components/SeletorParticipantes";
import { useCandidatosParticipantes, useVerificarConflito } from "@/modules/escala/hooks/useEscalas";

interface ModalAdicionarParticipanteProps {
  aberto: boolean;
  escalaId: string;
  ministerioId: string;
  carregando: boolean;
  erro: string | null;
  onConfirmar: (itens: { pessoaId: string; funcaoId: string }[]) => void;
  onCancelar: () => void;
}

/**
 * Seleção de candidatos elegíveis (integrantes ativos do ministério dono,
 * ainda sem participação ativa nesta escala) + aviso de conflito em lote
 * (uma única chamada a `escala_verificar_conflito` com todos os candidatos
 * exibidos, nunca pessoa por pessoa — seção 12 do relatório técnico). A
 * escolha em si usa o seletor compartilhado com a criação de escala.
 */
export function ModalAdicionarParticipante({
  aberto,
  escalaId,
  ministerioId,
  carregando,
  erro,
  onConfirmar,
  onCancelar,
}: ModalAdicionarParticipanteProps) {
  const { data: candidatos, isLoading } = useCandidatosParticipantes(ministerioId, escalaId);
  const verificarConflito = useVerificarConflito();
  const [selecionados, setSelecionados] = useState<Record<string, string>>({}); // pessoaId -> funcaoId
  const [conflitos, setConflitos] = useState<Record<string, boolean>>({});

  const lista = candidatos ?? [];
  const quantidadePessoas = new Set(lista.map((c) => c.pessoaId)).size;

  useEffect(() => {
    if (!aberto || !quantidadePessoas) return;
    verificarConflito.mutate(
      { escalaId, pessoaIds: [...new Set(lista.map((c) => c.pessoaId))] },
      { onSuccess: setConflitos },
    );
    // Roda só quando o modal abre com a lista carregada — não a cada seleção.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, escalaId, quantidadePessoas]);

  useEffect(() => {
    if (!aberto) {
      setSelecionados({});
      setConflitos({});
    }
  }, [aberto]);

  const nomesSelecionadosComConflito = [
    ...new Map(
      lista
        .filter((c) => selecionados[c.pessoaId] && conflitos[c.pessoaId])
        .map((c) => [c.pessoaId, c.nome] as const),
    ).values(),
  ];
  const total = Object.keys(selecionados).length;

  function confirmar() {
    const itens = Object.entries(selecionados).map(([pessoaId, funcaoId]) => ({ pessoaId, funcaoId }));
    if (itens.length) onConfirmar(itens);
  }

  return (
    <Modal titulo="Escalar participantes" aberto={aberto} onClose={onCancelar} carregando={carregando} alterado={total > 0}>
      <div className="flex flex-col gap-3">
        <SeletorParticipantes
          candidatos={lista}
          carregando={isLoading}
          selecionados={selecionados}
          onMudar={setSelecionados}
          conflitos={conflitos}
          listaRolavel
        />

        <AvisoConflito nomesComConflito={nomesSelecionadosComConflito} />

        {erro && (
          <p role="alert" className="rounded-md bg-danger/10 px-3 py-2 text-sm text-danger">
            {erro}
          </p>
        )}

        <div className="flex flex-col gap-1">
          <button
            type="button"
            onClick={confirmar}
            disabled={total === 0 || carregando}
            className="min-h-11 w-full rounded-xl bg-primary px-4 text-[15px] font-semibold text-on-primary transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {carregando ? "Adicionando..." : total === 0 ? "Escolha quem entra" : `Adicionar ${total} ${total === 1 ? "pessoa" : "pessoas"}`}
          </button>
          <BotaoVoltar onClick={onCancelar} disabled={carregando} className="min-h-11 w-full rounded-xl" />
        </div>
      </div>
    </Modal>
  );
}
